<template>
  <div>
    <InternalNotesWidget
      v-if="user.managerOrHigher"
      entityName="User"
      :entityId="clientId"
    />

    <HeaderBar>
      <v-icon slot="icon">person</v-icon>
      Client Users
      <template slot="buttons">
        <router-link :to="{ name: 'hrClients' }">
          Back to Clients
        </router-link>
      </template>
    </HeaderBar>

    <v-layout class="my-2">
      <v-flex class="my-4">
        <v-layout row class="mb-2">
          <v-flex xs12 md6>
            <h1 class="display-1 mb-2">
              {{ detailedClient.name }}
              <span v-if="detailedClient.deactivated">
                <v-icon color="red" large>block</v-icon>
                <span class="subtle">(Deactivated)</span>
              </span>
            </h1>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs12 md6 class="mb-2">
                <v-layout column>
                  <v-flex>
                    <h1 class="subheading">Staffing Access to Locations:</h1>
                    <div
                      v-if="!detailedClient.clientLocations || detailedClient.clientLocations.length == 0"
                      class="subtle mt-2"
                    >
                      No locations. Add one below
                    </div>
                    <div v-for="location in detailedClient.clientLocations">
                      <div style="display: inline-block;" class="location">
                        <router-link
                          target="_blank"
                          :to="{ name: 'clientBusinessDetail', params: { clientBusinessId: location.client_business_id } }"
                          >{{ location.name }}</router-link
                        >
                        <span class="location-remove">
                          <a
                            href="#"
                            @click.prevent="removeLocation(location.id)"
                            color="error"
                            class="red--text"
                          >
                            <v-icon color="red">
                              cancel
                            </v-icon>
                          </a>
                        </span>
                      </div>
                    </div>
                  </v-flex>

                  <v-flex>
                    <v-layout row wrap class="mb-2">
                      <v-flex xs12 md6>
                        <v-flex>
                          <v-autocomplete
                            v-model="selectedLocation"
                            label="Add Staffing Location"
                            :items="possibleLocations"
                            item-text="name"
                            item-value="id"
                            persistent-hint
                            clearable
                            hint="The client can manage shifts at the location chosen here."
                          :loading="gettingLocations"
                          :disabled="gettingLocations"
                          >
                          </v-autocomplete>
                        </v-flex>
                        <v-flex>
                          <v-btn
                            @click="addLocation"
                            :disabled="addingLocation || !selectedLocation"
                            :loading="addingLocation"
                            color="success"
                          >
                            Add Staffing Location to Client
                          </v-btn>
                        </v-flex>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
          </v-flex>

          <v-flex xs12 md6>
            <v-checkbox
              :disabled="detailedClient.administeredLocations.length > 0"
              v-model="isClientAdmin"
              label="Is Client Admin"
            />
            <div v-if="isClientAdmin">
              <h1 class="subheading">Admin For Locations:</h1>
              <div
                v-if="!detailedClient.administeredLocations || detailedClient.administeredLocations.length == 0"
                class="subtle mt-2"
              >
                No Administered Locations. Add one below
              </div>
              <div v-for="location in detailedClient.administeredLocations">
                <div style="display: inline-block;" class="location">
                  <router-link
                    target="_blank"
                    :to="{ name: 'clientBusinessDetail', params: { clientBusinessId: location.client_business_id } }"
                    >{{ location.name }}</router-link
                  >
                  <span class="location-remove">
                    <a
                      href="#"
                      @click.prevent="removeAdministeredLocation(location.id)"
                      color="error"
                      class="red--text"
                    >
                      <v-icon color="red">
                        cancel
                      </v-icon>
                    </a>
                  </span>
                </div>
              </div>
              <v-flex xs12 md6>
                <v-autocomplete
                  v-model="selectedAdminLocation"
                  label="Client Admin For Location"
                  :items="possibleAdminLocations"
                  item-text="name"
                  item-value="id"
                  persistent-hint
                  clearable
                  hint="TODO: The client can view financials for this location"
                >
                </v-autocomplete>
                <v-btn
                  @click="addAdminLocation"
                  :disabled="addingAdminLocation || !selectedAdminLocation"
                  :loading="addingAdminLocation"
                  color="success"
                >
                  Add Client Admin Location
                </v-btn>
              </v-flex>
              <v-flex xs12 md6 class="mt-4">
                <ApiField
                  label="Billing details (address, phone, etc.)"
                  storeAction="clients/update"
                  storeUpdate="clients/refreshDetailedClient"
                  paramName="client"
                  :object="detailedClient"
                  fieldName="billing_details"
                  :storeFieldValue="detailedClient.billingDetails"
                  fieldType="textarea"
                  box
                >
                </ApiField>
                <v-btn class="success">Save billing details</v-btn>
              </v-flex>
            </div>

            <div>
              <Referrer :user="detailedClient" />
            </div>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-divider class="my-4" />

    <v-layout row class="mb-2">
      <v-flex>
        <ApiField
          fieldType="switch"
          label="Eligible for Coordinated Coverage Requests"
          storeAction="clients/update"
          storeUpdate="clients/refreshDetailedClient"
          paramName="client"
          :object="detailedClient"
          fieldName="can_create_coordinated_coverage_requests"
          :storeFieldValue="detailedClient.canCreateCoordinatedCoverageRequests"
        />
        <ApiField
          fieldType="switch"
          label="Can auto-publish shifts"
          storeAction="clients/update"
          storeUpdate="clients/refreshDetailedClient"
          paramName="client"
          :object="detailedClient"
          fieldName="shift_creation_preapproved"
          :storeFieldValue="detailedClient.shiftCreationPreapproved"
        />
      </v-flex>
    </v-layout>

    <v-layout row wrap class="mb-2">
      <v-flex class="mr-2">
        <ApiField
          :hint="!detailedClient.firstName ? 'Some names did not import properly, please update this field' : ''"
          label="First Name"
          storeAction="clients/update"
          storeUpdate="clients/refreshDetailedClient"
          paramName="client"
          :object="detailedClient"
          fieldName="first_name"
          :storeFieldValue="detailedClient.firstName"
        >
        </ApiField>
      </v-flex>
      <v-flex>
        <ApiField
          :hint="!detailedClient.lastName ? 'Some names did not import properly, please update this field' : ''"
          label="Last Name"
          storeAction="clients/update"
          storeUpdate="clients/refreshDetailedClient"
          paramName="client"
          :object="detailedClient"
          fieldName="last_name"
          :storeFieldValue="detailedClient.lastName"
        >
        </ApiField>
      </v-flex>
    </v-layout>

    <v-layout row wrap class="mb-2">
      <v-flex>
        <ApiField
          :disabled="!updatingEmail"
          hint="This is the user's login. Only change this if you are sure it's correct."
          label="Email / Login"
          storeAction="clients/update"
          storeUpdate="clients/refreshDetailedClient"
          paramName="client"
          :object="detailedClient"
          fieldName="email"
          :storeFieldValue="detailedClient.email"
        >
        </ApiField>
      </v-flex>

      <v-flex>
        <v-btn
          v-if="!updatingEmail"
          color="warning"
          @click="updatingEmail = !updatingEmail"
          >Change Email</v-btn
        >
        <v-btn
          v-if="updatingEmail"
          color="warning"
          @click="updatingEmail = !updatingEmail"
          >Lock Email</v-btn
        >

        <v-dialog v-if="detailedClient.deactivated" v-model="reactivateDialog" max-width="300">
          <v-btn
            v-if="detailedClient.deactivated"
            color="success"
            slot="activator"
          >
            Reactivate Access
          </v-btn>
          <v-card>
            <v-card-title>
              <h2 class="headline">Reactivate Access?</h2>
            </v-card-title>
            <v-card-text>
              <p>
                After reactivating this user, they will need to reset their
                password. You can send them password reset instructions on this
                page.
              </p>
            </v-card-text>
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn color="grey" flat @click="reactivateDialog = false"
                >Nevermind</v-btn
              >
              <v-btn
                color="error"
                :disabled="reactivating"
                :loading="reactivating"
                @click="reactivateUser"
                >Reactivate!</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-if="!detailedClient.deactivated" v-model="deactivateDialog" max-width="300">
          <v-btn
            v-if="!detailedClient.deactivated"
            color="error"
            slot="activator"
          >
            Revoke Access
          </v-btn>
          <v-card>
            <v-card-title>
              <h2 class="headline">Revoke Access?</h2>
            </v-card-title>
            <v-card-text>
              <p>
                This will deactivate this Client User's account. It will change
                their password and disable their ability to reset their own
                password. While they are in the deactivated state, they will not
                be able to access this system at all.
              </p>
              <p>
                None of the data associated with this account will be changed or
                lost.
              </p>
              <p>
                You can reactivate this user at any time.
              </p>
            </v-card-text>
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn color="grey" flat @click="deactivateDialog = false"
                >Nevermind</v-btn
              >
              <v-btn
                color="error"
                :disabled="deactivating"
                :loading="deactivating"
                @click="deactivateUser"
                >Deactivate!</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-if="user.role == 'superadmin'"
          v-model="deleteDialog"
          max-width="300"
        >
          <v-btn color="error" slot="activator">Delete Client User</v-btn>
          <v-card>
            <v-card-title>
              <h2 class="headline">Delete Client User?</h2>
            </v-card-title>
            <v-card-text>
              <p>This will delete this client user from the system entirely.</p>
            </v-card-text>
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn color="grey" flat @click="deleteDialog = false"
                >Nevermind</v-btn
              >
              <v-btn
                color="error"
                :disabled="deleting"
                :loading="deleting"
                @click="deleteUser"
                >Delete!</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-layout>

    <v-layout row wrap v-if="user.managerOrHigher" class="mb-2">
      <v-flex class="mr-2">
        <v-text-field
          :disabled="!updatingPassword"
          label="Password"
          type="password"
          v-model="newPassword"
        >
        </v-text-field>
      </v-flex>
      <v-flex>
        <v-text-field
          :disabled="!updatingPassword"
          label="Password Confirmation"
          type="password"
          v-model="newPasswordConfirmation"
          :error-messages="(newPassword && newPasswordConfirmation && !passwordsMatch) ? 'The password and confirmation do no match' : '' "
        >
        </v-text-field>
      </v-flex>
      <v-flex>
        <v-btn
          v-if="!updatingPassword"
          color="warning"
          @click="updatingPassword = !updatingPassword"
          >Change Password</v-btn
        >
        <v-btn
          v-if="updatingPassword"
          color="warning"
          @click="saveNewPassword"
          :disabled="!(newPassword && newPasswordConfirmation) || !passwordsMatch"
          >Save and Update Password</v-btn
        >
        <v-btn
          color="grey"
          flat
          @click="cancelUpdatingPassword"
          v-if="updatingPassword"
          >Cancel</v-btn
        >
      </v-flex>
    </v-layout>

    <v-divider class="my-2"></v-divider>

    <v-flex>
      <v-dialog v-model="passwordResetDialog" max-width="300">
        <v-btn color="primary" slot="activator"
          >Send Password Reset Instructions</v-btn
        >
        <v-card>
          <v-card-title>
            <h2 class="headline">Reset Password?</h2>
          </v-card-title>
          <v-card-text>
            <p>
              This will send an email to {{ detailedClient.email }} with
              instructions to reset their password. The password will not be
              changed until they proceed with the reset instructions.
            </p>
          </v-card-text>
          <v-spacer></v-spacer>
          <v-card-actions>
            <v-btn color="grey" flat @click="passwordResetDialog = false"
              >Nevermind</v-btn
            >
            <v-btn color="success" @click="sendPasswordReset"
              >Send the Email</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="welcomeEmailDialog" max-width="300">
        <v-btn color="primary" slot="activator">Send Welcome Email</v-btn>
        <v-card>
          <v-card-title>
            <h2 class="headline">Send Welcome Email?</h2>
          </v-card-title>
          <v-card-text>
            <p>
              This will send an initial welcome email to {{ detailedClient.email
              }} with instructions on setting up their account and how to find
              onboarding resources.
            </p>
          </v-card-text>
          <v-spacer></v-spacer>
          <v-card-actions>
            <v-btn color="grey" flat @click="welcomeEmailDialog = false"
              >Nevermind</v-btn
            >
            <v-btn color="success" @click="sendWelcomeEmail"
              >Send the Email</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </div>
</template>

<script>
  import {mapState} from 'vuex';
  import HeaderBar from '@/components/base/header-bar.vue';
  import ApiField from '@/components/base/api-field.vue';
  import collectionsApi from '@/api/collections';
  import emailsApi from '@/api/emails';
  import Referrer from '@/components/hr/referrer.vue'

  export default {
    components: {
      HeaderBar,
      ApiField,
      Referrer,
    },
    computed: {
      ...mapState('userAuth', ['user']),
      ...mapState('clients', ['detailedClient']),
      isClientAdmin: {
        get() {
          return (
            this.clientAdminChecked ||
            this.detailedClient.administeredLocations.length > 0
          );
        },
        set(val) {
          val
            ? (this.clientAdminChecked = true)
            : (this.clientAdminChecked = false);
        },
      },
      clientId() {
        return this.$route.params.clientId;
      },
      passwordsMatch() {
        if (this.newPassword == this.newPasswordConfirmation) {
          return true;
        }
        return false;
      },
    },
    created() {
      this.setUp();
    },
    watch: {},
    data: function() {
      return {
        clientAdminChecked: false,
        addingLocation: false,
        addingAdminLocation: false,
        removingLocation: true,
        removingAdministeredLocation: true,
        gettingLocations: false,
        updatingEmail: false,
        updatingPassword: false,
        passwordResetDialog: false,
        deactivateDialog: false,
        reactivateDialog: false,
        deleteDialog: false,
        deleting: false,
        deactivating: false,
        reactivating: false,
        welcomeEmailDialog: false,
        possibleLocations: [],
        possibleAdminLocations: [],
        selectedLocation: null,
        selectedAdminLocation: null,
        newPassword: null,
        newPasswordConfirmation: null,
      };
    },
    methods: {
      setUp() {
        this.$store.dispatch('clients/getClient', this.$route.params.clientId);
        this.getPossibleLocations();
      },
      saveNewPassword() {
        this.$store
          .dispatch('clients/update', {
            id: this.$route.params.clientId,
            client: {
              password: this.newPassword,
              password_confirmation: this.newPasswordConfirmation,
            },
          })
          .then(res => {
            this.$store.dispatch('global/showSnackbarSuccess', {
              message: 'Password set! Be sure to let the client know.',
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.dispatch('global/showSnackbarError', {
              message: 'Something went wrong setting that password...',
            });
          })
          .finally(res => {
            this.cancelUpdatingPassword();
          });
      },
      cancelUpdatingPassword() {
        this.newPassword = null;
        this.newPasswordConfirmation = null;
        this.updatingPassword = false;
      },
      getPossibleLocations() {
        this.gettingLocations = true;
        collectionsApi.getLocations(locations => {
          this.possibleLocations = locations.filter(l => {
            return !this.detailedClient.clientLocations
              .map(cl => cl.id)
              .includes(l.id);
          });
          this.gettingLocations = false;
          this.possibleAdminLocations = locations;
        });
      },
      deactivateUser() {
        this.deactivating = true;
        this.$store
          .dispatch('clients/deactivate', this.detailedClient.id)
          .then(r => {
            this.$store.dispatch('clients/getClient', this.$route.params.clientId)
          })
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.deactivating = false;
            this.deactivateDialog = false;
          });
      },
      reactivateUser() {
        this.reactivating = true;
        this.$store
          .dispatch('clients/reactivate', this.detailedClient.id)
          .then(r => {
            this.$store.dispatch('clients/getClient', this.$route.params.clientId)
          })
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.reactivating = false;
            this.reactivateDialog = false;
          });
      },
      deleteUser() {
        this.deleting = true;
        this.$store
          .dispatch('clients/delete', this.detailedClient.id)
          .then(r => {})
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.deleting = false;
            this.deleteDialog = false;
            this.$store.dispatch('global/showSnackbarSuccess', {
              message: 'Client user deleted!',
            });
            this.$router.push({name: 'hrClients'});
          });
      },
      addLocation() {
        this.addingLocation = true;
        this.$store
          .dispatch('clients/addLocation', {
            clientId: this.detailedClient.id,
            locationId: this.selectedLocation,
          })
          .then(r => {
            this.$store.dispatch(
              'clients/getClient',
              this.$route.params.clientId,
            );
          })
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.getPossibleLocations();
            this.addingLocation = false;
            this.selectedLocation = null;
          });
      },
      addAdminLocation() {
        this.addingAdminLocation = true;
        this.$store
          .dispatch('clients/addAdminLocation', {
            clientId: this.detailedClient.id,
            locationId: this.selectedAdminLocation,
          })
          .then(r => {
            this.$store.dispatch(
              'clients/getClient',
              this.$route.params.clientId,
            );
          })
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.getPossibleLocations();
            this.addingAdminLocation = false;
            this.selectedAdminLocation = null;
          });
      },
      removeLocation(id) {
        this.removingLocation = true;
        this.$store
          .dispatch('clients/removeLocation', {
            clientId: this.detailedClient.id,
            locationId: id,
          })
          .then(r => {
            this.$store.dispatch(
              'clients/getClient',
              this.$route.params.clientId,
            );
          })
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.getPossibleLocations();
            this.removingLocation = false;
          });
      },
      removeAdministeredLocation(id) {
        this.removingAdministeredLocation = true;
        this.$store
          .dispatch('clients/removeAdminLocation', {
            clientId: this.detailedClient.id,
            locationId: id,
          })
          .then(r => {
            this.$store.dispatch(
              'clients/getClient',
              this.$route.params.clientId,
            );
          })
          .catch(error => {
            console.log(error);
          })
          .finally(r => {
            this.getPossibleLocations();
            this.removingAdministeredLocation = false;
          });
      },
      sendWelcomeEmail() {
        emailsApi.sendWelcomeEmail(
          this.detailedClient.id,
          res => {
            this.welcomeEmailDialog = false;
            this.$store.dispatch('global/showSnackbarSuccess', {
              message: 'A welcome email has been sent!',
            });
          },
          error => {
            this.welcomeEmailDialog = false;
            this.$store.dispatch('global/showSnackbarError', {
              message: 'Something went wrong sending that welcome email.',
            });
          },
        );
      },
      sendPasswordReset() {
        this.$store
          .dispatch('userAuth/requestPasswordReset', this.detailedClient.email)
          .then(() => {
            this.passwordResetDialog = false;
            this.$store.dispatch('global/showSnackbarSuccess', {
              message: 'A password reset email has been sent!',
            });
          })
          .catch(err => {
            this.passwordResetDialog = false;
            this.$store.dispatch('global/showSnackbarError', {
              message: 'Something went wrong sending that password reset.',
            });
          });
      },
    },
  };
</script>

<style scoped>
  .location-remove {
    visibility: hidden;
    animation: none;
  }

  .location:hover .location-remove {
    visibility: visible;
    display: inline-block;
  }
</style>
